import { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Person, Leaderboard } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataTable } from './data-table';
import type { UserStatsResponse } from '@store/generated/jotcoach';

interface JotCoachUserActivityTableProps {
  users: UserStatsResponse[];
  loading?: boolean;
}

export function JotCoachUserActivityTable({
  users,
  loading = false,
}: JotCoachUserActivityTableProps) {
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        key: 'rank' as keyof UserStatsResponse,
        label: 'Rank',
        render: (_: unknown, row: UserStatsResponse, index?: number) => {
          const rank = (index || 0) + 1;
          return (
            <Typography variant="body2" fontWeight="bold" color="text.secondary">
              #{rank}
            </Typography>
          );
        },
      },
      {
        key: 'user_name' as keyof UserStatsResponse,
        label: 'User',
        render: (_: unknown, row: UserStatsResponse) => (
          <Box display="flex" alignItems="center" gap={1}>
            <Person fontSize="small" color="action" />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {row.user_name || 'Unknown User'}
              </Typography>
              {row.user_email && (
                <Typography variant="caption" color="text.secondary">
                  {row.user_email}
                </Typography>
              )}
              {!row.user_email && (
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {row.user_id.substring(0, 12)}...
                </Typography>
              )}
            </Box>
          </Box>
        ),
      },
      {
        key: 'organization_name' as keyof UserStatsResponse,
        label: 'Organization',
        render: (_: unknown, row: UserStatsResponse) => (
          <Box>
            <Typography variant="body2">
              {row.organization_name || 'Unknown Organization'}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
              {row.organization_id.substring(0, 12)}...
            </Typography>
          </Box>
        ),
      },
      {
        key: 'session_count' as keyof UserStatsResponse,
        label: 'Sessions',
        render: (value: unknown) => (
          <Chip
            label={typeof value === 'number' ? value.toLocaleString() : String(value)}
            color="primary"
            size="small"
          />
        ),
      },
      {
        key: 'feedback_count' as keyof UserStatsResponse,
        label: 'Total Feedback',
        render: (value: unknown) => (
          <Typography variant="body2">
            {typeof value === 'number' ? value.toLocaleString() : String(value)}
          </Typography>
        ),
      },
      {
        key: 'feedback_rate' as keyof UserStatsResponse,
        label: 'Feedback Rate',
        render: (value: unknown) => {
          const rate = typeof value === 'number' ? value : 0;
          return (
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {rate.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                per session
              </Typography>
            </Box>
          );
        },
      },
    ],
    [],
  );

  const handleRowClick = (user: UserStatsResponse) => {
    navigate(`/admin-v2/jotcoach/sessions?user_id=${user.user_id}`);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Leaderboard fontSize="large" color="primary" />
        <Box>
          <Typography variant="h5" fontWeight="medium">
            User Activity Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Top 20 most active providers by session count
          </Typography>
        </Box>
      </Box>

      <DataTable
        title=""
        columns={columns}
        data={users}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage="No user activity data available for the selected period"
      />
    </Box>
  );
}
